import React, { useEffect } from 'react';
import { useParseContractContext } from 'features/tasks-forms';
import {
  PlanTask,
  useAiLauncherService, usePlanTasksService,
  useProjectDetailsFieldsService, ValidationError,
} from 'nekst-api';
import { useProjectContext } from '../../../view/ProjectContext';
import { LoadingAnimation, usePopupManager } from 'ui-builder';
import { VerifyDataEntry } from './types';
import LaunchFromContractSteps, { LaunchFromContractStepId } from '../LaunchFromContractSteps';
import { NextButton } from '../../Buttons';
import { useParams } from 'react-router-dom';
import useLaunchFromContractNavigator from '../launchFromContractNavigator';
import { ApplyParseDataValidationPopup, DetailFieldsList } from '../../Shared/VerifyDataComponents';
import VerifyDataLayout from '../../Shared/VerifyDataLayout';
import ValidateDetailsFieldsTour from './ValidateDetailsFieldsTour';

function useVerifyDataEntriesLoader() {
  const projectContext = useProjectContext();

  const projectType = projectContext.data?.type;

  const detailsFieldsService = useProjectDetailsFieldsService();

  const parsedContractContext = useParseContractContext();

  const [planTasks, setPlanTasks] = React.useState<PlanTask[]>();

  const [verifyDataEntries, setVerifyDataEntries] = React.useState<VerifyDataEntry[]>();

  const planTaskService = usePlanTasksService();

  useEffect(() => {
    if (projectContext.data?.planId) {
      planTaskService.getPlanTasks(projectContext.data.planId)
        .then((tasks) => {
          setPlanTasks(tasks);
        });
    }
  }, [projectContext.data?.planId]);

  const getData = async () => {
    if (!(projectType && parsedContractContext && planTasks)) {
      return [];
    } else {

      const detailsFieldsItems = (await detailsFieldsService.getFieldsApplicableForPlan(projectContext.data!.planId))
        .map((detailsField) => {
          let name = detailsField.name;
          if (detailsField.isProjectStartDate) {
            name += ' (Start Date)';
          } else if (detailsField.isProjectEndDate) {
            name += ' (End Date)';
          }

          return {
            detailsField,
            name,
            primary: detailsField.isProjectStartDate || detailsField.isProjectEndDate,
            parsedValue: parsedContractContext.parseResult.detailsFields[detailsField.id],
            relatedTask: planTasks.find((item) => item.linkedFieldId === detailsField.id),
          };
        });

      const startDateField = detailsFieldsItems.find((item) => item.detailsField.isProjectStartDate);
      const endDateField = detailsFieldsItems.find((item) => item.detailsField.isProjectEndDate);

      const result = [];

      if (startDateField) {
        result.push(startDateField);
      }

      if (endDateField) {
        result.push(endDateField);
      }

      result.push(
        ...detailsFieldsItems.filter(
          (item) => !item.detailsField.isProjectStartDate
            && !item.detailsField.isProjectEndDate
        )
      );

      return result as VerifyDataEntry[];
    }
  };

  useEffect(() => {
    if (parsedContractContext && projectType && planTasks) {
      getData()
        .then((data) => {
          setVerifyDataEntries(data);
        });
    }
  }, [projectType, parsedContractContext, planTasks]);

  return {
    verifyDataEntries,
    planTasks,
    fileLink: parsedContractContext?.parseResult.fileLink
  };
}

export default function ValidateDefinedContractDataPage() {

  const {
    verifyDataEntries,
    fileLink
  } = useVerifyDataEntriesLoader();

  const params = useParams<{ id: string }>();

  const projectId = parseInt(params.id!, 10);

  const aiLauncherService = useAiLauncherService();

  const launchFromContractNavigator = useLaunchFromContractNavigator();

  const projectContext = useProjectContext();

  const popupManager = usePopupManager();

  if (verifyDataEntries) {
    return (
      <VerifyDataLayout
        roadmap={<LaunchFromContractSteps currentStep={LaunchFromContractStepId.VERIFY_DATA} />}
        nextButton={(
          <NextButton
            onClick={async () => {
              try {
                const projectData = await aiLauncherService.applyDefinedData(projectId);
                projectContext._updateData(projectData);

                setTimeout(() => {
                  launchFromContractNavigator.openSelectGroupsPage();
                });

              } catch (e) {
                if (e instanceof ValidationError) {
                  popupManager.openPopup(<ApplyParseDataValidationPopup error={e} />);
                }
              }

            }}
            key="next-button"
          />
        )}
        detailFieldsList={<DetailFieldsList verifyDataEntries={verifyDataEntries} />}
        fileLink={fileLink}
      >
        <ValidateDetailsFieldsTour />
      </VerifyDataLayout>
    );
  } else {
    return (<LoadingAnimation />);
  }
}
